import { apiFetchData, withAuthHeader, type AuthRetry } from '@/shared/lib/api';
import { useAuthStore } from '@/features/auth/store';
import { wrapSubscriptionError } from './errors';
import type {
  SubscriptionResponse,
  PaidPlan,
  PlanInfo,
  ChangePlanPreviewRequest,
  ChangePlanPreviewResponse,
  CancelRequest,
  InvoiceListResponse,
  UpcomingInvoiceResponse,
} from './types';

function getAuthRetry(): AuthRetry {
  return {
    getToken: () => useAuthStore.getState().token,
    refreshTokens: () => useAuthStore.getState().refreshTokens(),
  };
}

function requireToken(token: string | null): string {
  if (!token) throw new Error('No token');
  return token;
}

export const subscriptionApi = {
  /** Public — no auth needed */
  async getPlans(): Promise<PlanInfo[]> {
    return wrapSubscriptionError(
      apiFetchData<PlanInfo[]>(`/api/subscription/plans`, { method: 'GET' }),
    );
  },

  async getSubscription(token: string | null): Promise<SubscriptionResponse> {
    const t = requireToken(token);
    return wrapSubscriptionError(
      apiFetchData<SubscriptionResponse>(
        `/api/owner/subscription`,
        { method: 'GET', headers: withAuthHeader(undefined, t) },
        getAuthRetry(),
      ),
    );
  },

  async createCheckoutSession(
    token: string | null,
    plan: PaidPlan,
    returnUrl?: string,
  ): Promise<{ clientSecret: string }> {
    const t = requireToken(token);
    return wrapSubscriptionError(
      apiFetchData<{ clientSecret: string }>(
        `/api/owner/subscription/checkout`,
        {
          method: 'POST',
          headers: withAuthHeader(undefined, t),
          body: {
            plan,
            returnUrl:
              returnUrl ||
              `${window.location.origin}/app?subscription=success&session_id={CHECKOUT_SESSION_ID}`,
          },
        },
        getAuthRetry(),
      ),
    );
  },

  async createPortalSession(
    token: string | null,
    returnUrl?: string,
  ): Promise<{ url: string }> {
    const t = requireToken(token);
    return wrapSubscriptionError(
      apiFetchData<{ url: string }>(
        `/api/owner/subscription/portal`,
        {
          method: 'POST',
          headers: withAuthHeader(undefined, t),
          body: { returnUrl: returnUrl || `${window.location.origin}/app/billing` },
        },
        getAuthRetry(),
      ),
    );
  },

  async syncSubscription(
    token: string | null,
    sessionId: string,
  ): Promise<SubscriptionResponse> {
    const t = requireToken(token);
    return wrapSubscriptionError(
      apiFetchData<SubscriptionResponse>(
        `/api/owner/subscription/sync`,
        {
          method: 'POST',
          headers: withAuthHeader(undefined, t),
          body: { sessionId },
        },
        getAuthRetry(),
      ),
    );
  },

  async previewPlanChange(
    token: string | null,
    body: ChangePlanPreviewRequest,
  ): Promise<ChangePlanPreviewResponse> {
    const t = requireToken(token);
    return wrapSubscriptionError(
      apiFetchData<ChangePlanPreviewResponse>(
        `/api/owner/subscription/change-plan/preview`,
        { method: 'POST', headers: withAuthHeader(undefined, t), body },
        getAuthRetry(),
      ),
    );
  },

  async changePlan(
    token: string | null,
    body: ChangePlanPreviewRequest,
  ): Promise<SubscriptionResponse> {
    const t = requireToken(token);
    return wrapSubscriptionError(
      apiFetchData<SubscriptionResponse>(
        `/api/owner/subscription/change-plan`,
        { method: 'POST', headers: withAuthHeader(undefined, t), body },
        getAuthRetry(),
      ),
    );
  },

  async cancel(token: string | null, body: CancelRequest): Promise<SubscriptionResponse> {
    const t = requireToken(token);
    return wrapSubscriptionError(
      apiFetchData<SubscriptionResponse>(
        `/api/owner/subscription/cancel`,
        { method: 'POST', headers: withAuthHeader(undefined, t), body },
        getAuthRetry(),
      ),
    );
  },

  async resume(token: string | null): Promise<SubscriptionResponse> {
    const t = requireToken(token);
    return wrapSubscriptionError(
      apiFetchData<SubscriptionResponse>(
        `/api/owner/subscription/resume`,
        { method: 'POST', headers: withAuthHeader(undefined, t) },
        getAuthRetry(),
      ),
    );
  },

  async listInvoices(
    token: string | null,
    params: { limit?: number; startingAfter?: string | null } = {},
  ): Promise<InvoiceListResponse> {
    const t = requireToken(token);
    const search = new URLSearchParams();
    search.set('limit', String(params.limit ?? 10));
    if (params.startingAfter) search.set('startingAfter', params.startingAfter);
    return wrapSubscriptionError(
      apiFetchData<InvoiceListResponse>(
        `/api/owner/subscription/invoices?${search.toString()}`,
        { method: 'GET', headers: withAuthHeader(undefined, t) },
        getAuthRetry(),
      ),
    );
  },

  async getUpcomingInvoice(token: string | null): Promise<UpcomingInvoiceResponse> {
    const t = requireToken(token);
    return wrapSubscriptionError(
      apiFetchData<UpcomingInvoiceResponse>(
        `/api/owner/subscription/upcoming-invoice`,
        { method: 'GET', headers: withAuthHeader(undefined, t) },
        getAuthRetry(),
      ),
    );
  },
};
